import { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { Alert } from "reactstrap";
import { Link } from "react-router-dom";
import { useDocumentCreateMutation, useDocumentDeleteMutation, useDocumentFindAllQuery } from "store";

type ImageUploaderProps = {
  tableName: string;
  connectionId: number | string;
  valueGroup?: string;
  accept?: string[];
  multiple?: boolean;
};

const ImageUploader = ({
  tableName,
  connectionId,
  valueGroup = "images",
  accept = [".jpeg", ".jpg", ".png"],
  multiple = true,
}: ImageUploaderProps) => {
  const [images, setImages] = useState<any[]>([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const { data: existingData } = useDocumentFindAllQuery(
    `?table_name=${tableName}&connection_id=${connectionId}`
  );

  const [documentCreate] = useDocumentCreateMutation();
  const [documentDelete] = useDocumentDeleteMutation();

  useEffect(() => {
    if (existingData?.data) {
      const mapped = existingData.data.map((img: any) => ({
        ...img,
        preview: img.fileUrl,
        status: "uploaded",
        isExisting: true,
      }));
      setImages(mapped);
    }
  }, [existingData]);

  const handleAcceptedFiles = (files: any[]) => {
    const mappedFiles = files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
        status: "pending",
        isExisting: false,
      })
    );

    setImages((prev) => [...prev, ...mappedFiles]);
    mappedFiles.forEach(uploadImage);
  };

  const uploadImage = async (file: any) => {
    const formData = new FormData();
    formData.append("files", file);
    formData.append("table_name", tableName);
    formData.append("connection_id", String(connectionId));
    formData.append("value_group", valueGroup);

    try {
      const response: any = await documentCreate(formData).unwrap();
      setImages((prev) =>
        prev.map((img) =>
          img === file
            ? { ...img, status: "uploaded", url: response.data?.[0]?.url, id: response.data?.[0]?.id }
            : img
        )
      );
    } catch (err) {
      console.error("Upload failed", err);
      setImages((prev) =>
        prev.map((img) => (img === file ? { ...img, status: "error" } : img))
      );
    }
  };

  const handleRemoveFile = async (index: number) => {
    const img = images[index];
    if (img.isExisting && img.id) {
      try {
        await documentDelete(img.id).unwrap();
      } catch (err) {
        console.error("Delete failed", err);
      }
    }
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div>
      <Dropzone
        multiple={multiple}
        accept={{ "image/*": accept }}
        onDrop={handleAcceptedFiles}
      >
        {({ getRootProps, getInputProps }) => (
          <div className="dropzone" {...getRootProps()}>
            <input {...getInputProps()} />
            <div className="dz-message needsclick mt-2">
              <div className="mb-3">
                <i className="display-4 text-muted mdi mdi-cloud-upload-outline" />
              </div>
              <Alert color="info" className="mb-0 fs-5">
                <i className="mdi mdi-alert-circle-outline me-2" />
                Файлын төрөл:
                <Link to="#" className="alert-link ms-1">
                  {accept.join(", ")}
                </Link>
              </Alert>
            </div>
          </div>
        )}
      </Dropzone>

      <div className="zoom-gallery d-flex gap-3 flex-row flex-wrap justify-content-start mt-3">
        {images.map((file, i) => (
          <div key={i} className="position-relative">
            <img
              src={file.preview}
              alt={file.name || "Uploaded Image"}
              className="img-thumbnail"
              style={{ width: 100, height: 100, objectFit: "cover", cursor: "pointer" }}
              onClick={() => {
                setPhotoIndex(i);
                setLightboxOpen(true);
              }}
            />
            <button
              type="button"
              className="btn btn-sm btn-danger position-absolute"
              style={{ top: 2, right: 2 }}
              onClick={() => handleRemoveFile(i)}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {lightboxOpen && images.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
        }}>
          <button onClick={() => setLightboxOpen(false)} style={{ position: 'absolute', top: 10, right: 10 }}>×</button>
          <img src={images[photoIndex].preview} style={{ maxHeight: '90%', maxWidth: '90%' }} />
        </div>
      )}
    </div>
  );
};

export default ImageUploader;

function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}
